/**
 * Temporary, session-scoped storage of a guest candidate's basic contact details
 * so that applying to several vacancies in one browser session does not mean
 * re-typing the same four fields.
 *
 * GDPR: only the fields listed in ALLOWED_FIELDS are ever written. No CV, cover
 * letter, supporting documents, screening answers, address or consent flags are
 * persisted. sessionStorage is used (not localStorage) so the browser discards
 * everything when the tab/session ends.
 */
export const QUICK_APPLY_SESSION_KEY = 'quickApplyGuestDetails';

// PhoneCountryCode and countryName are part of the phone number - without them
// the phone input cannot restore the right dial code and flag.
const ALLOWED_FIELDS = ['firstName', 'lastName', 'email', 'Phone', 'PhoneCountryCode', 'countryName'];

const asString = (value) => (typeof value === 'string' ? value : '');

/** Keeps only the allowed fields, coerced to strings. */
const pickAllowedFields = (source) =>
  ALLOWED_FIELDS.reduce((acc, field) => {
    acc[field] = asString(source?.[field]);
    return acc;
  }, {});

const hasAnyValue = (details) => ALLOWED_FIELDS.some((field) => details[field].trim() !== '');

/**
 * Stores the guest's name, email and phone number for the rest of the browser session.
 * Storage failures (private browsing, disabled storage, quota) are ignored - the
 * prefill is a convenience and must never break an application.
 */
export function saveGuestDetails(details) {
  const allowed = pickAllowedFields(details);
  if (!hasAnyValue(allowed)) return;

  try {
    window.sessionStorage.setItem(QUICK_APPLY_SESSION_KEY, JSON.stringify(allowed));
  } catch (e) {
    // no-op
  }
}

/**
 * Returns the details stored earlier in this browser session, or null when there
 * is nothing usable - in which case the form behaves exactly as it does today.
 */
export function getGuestDetails() {
  try {
    const raw = window.sessionStorage.getItem(QUICK_APPLY_SESSION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    const allowed = pickAllowedFields(parsed);
    return hasAnyValue(allowed) ? allowed : null;
  } catch (e) {
    return null;
  }
}

export function clearGuestDetails() {
  try {
    window.sessionStorage.removeItem(QUICK_APPLY_SESSION_KEY);
  } catch (e) {
    // no-op
  }
}
